import { z } from "zod";
import type { Edge } from "@xyflow/react";

const statusSchema = z.enum(["healthy", "warning", "critical"]);

const kpiAlertConnection = z.object({
  alertId: z.string(),
  weight: z.number().min(0).max(100).optional().default(0),
});

const kpi = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional().default(""),
  alerts: z.array(z.string()).optional().default([]),
  alertConnections: z.array(kpiAlertConnection).optional().default([]),
  weight: z.number().optional().default(0),
  system: z.boolean().optional().default(false),
  thresholds: z.object({
    warning: z.number(),
    critical: z.number(),
  }),
  metrics: z.object({
    score: z.number().optional(),
    status: statusSchema.optional(),
    linkedAlerts: z.number().optional(),
    activeAlerts: z.number().optional(),
    activeWarningAlerts: z.number().optional(),
    activeCriticalAlerts: z.number().optional(),
  }).optional().default({}),
});

const businessService = z.object({
  id: z.string(),
  label: z.string().trim().min(1, "Label is required"),
  status: statusSchema,
  owner: z.string(),
  sla: z.number(),
  revenueWeight: z.number(),
  businessCritical: z.number(),
  kpis: z.array(kpi),
  metrics: z.object({
    riskForecast: z.number(),
    score: z.number(),
    slo: z.number(),
    linkedAlerts: z.number().optional(),
    activeAlerts: z.number().optional(),
    kpiHealthy: z.number().optional(),
    kpiWarning: z.number().optional(),
    kpiCritical: z.number().optional(),
  }).optional()
});

const node = z.object({
  id: z.string(),
  type: z.string().optional(),
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
  data: businessService,
});

const serviceTemplate = z.object({
  id: z.string(),
  name: z.string(),
  system: z.boolean().default(false),
  description: z.string(),
  kpis: z.array(kpi),
  type: z.enum(["saas", "retail", "internal_it", "banking"]),
});

const edge = z
  .object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    type: z.string().optional(),
  })
  .loose() as z.ZodType<Edge>;

const userRole = z.enum(["admin"]);
const userStatus = z.enum(["pending", "confirmed"]);

const commonAuth = z.object({
  ready: z.boolean(),
});

const apiKeyIntegrationAuth = commonAuth.extend({
  type: z.literal("apiKey"),
  apiKey: z.string().trim().min(1, "Api key is required"),
});

const apiKeyAndRegionAuth = commonAuth.extend({
  type: z.literal("apiKeyAndRegion"),
  apiKey: z.string().trim().min(1, "Api key is required"),
  region: z.string().trim().min(1, "Region is required"),
});

const oauthIntegrationAuth = commonAuth.extend({
  type: z.literal("oauth"),
  region: z.string().trim().min(1, "Region is required"),
  refreshToken: z.string(),
  accessToken: z.string(),
  expiresAt: z.number(),
});

const integrationAuth = {
  oauth: oauthIntegrationAuth,
  apiKey: apiKeyIntegrationAuth,
  apiKeyAndRegion: apiKeyAndRegionAuth,
};

const genericIntegrationAuth = z.union([
  apiKeyIntegrationAuth,
  oauthIntegrationAuth,
  apiKeyAndRegionAuth,
]);

export const systemAlertSchemas = z.enum([
  "system-sentry",
  "system-coralogix",
  "system-datadog",
  "system-pagerduty",
  "system-opsgenie",
  "system-prometheus-alertmanager",
  "system-grafana",
  "system-splunk",
  "system-cloudwatch",
  "system-azure-monitor",
  "system-gcp-monitoring",
  "system-newrelic",
]);

const sentryIntegrationMetadata = z.object({
  type: z.literal("system-sentry"),
  organization: z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    dateCreated: z.string(),
  }),
  username: z.string(),
});

const genericIntegrationMetadata = z.union([sentryIntegrationMetadata]);

const integrationMetadata = {
  sentry: sentryIntegrationMetadata,
};

export const serviceTemplateId = z.string().trim().min(1, "Template is required");

export const Schemas = {
  status: statusSchema,
  businessService,
  nodeSchema: node,
  edge,
  integrationAuth,
  serviceMap: z.object({
    nodes: z.array(node),
    edges: z.array(edge),
  }),
  kpi,
  serviceTemplate,
  systemAlertSchemas,
  genericIntegrationAuth,
  genericIntegrationMetadata,
  integrationMetadata,
  integration: z.object({
    name: z.string().trim().min(1, "Name is required"),
    id: z.string(),
    alertSchemaId: z.string(),
    system: z.boolean().default(false),
    url: z.string(),
    auth: genericIntegrationAuth.optional(),
    metadata: genericIntegrationMetadata.optional(),
    description: z.string().optional(),
  }),
  dashboard: z.object({
    name: z.string().trim().min(1, "Name is required"),
    description: z.string(),
    id: z.string().trim().min(1, "Id is required"),
    metrics: z.object({
      platformHealth: z.number(),
      activeEvents: z.number(),
      riskTrending: z.number(),
    }),
  }),
  alertEvent: z.object({
    id: z.string(),
    timestamp: z.number(),
    alertId: z.string(),
    status: statusSchema,
    metadata: z.string(),
  }),
  alert: z.object({
    name: z.string().trim().min(1, "Name is required"),
    description: z.string().optional(),
    id: z.string().trim().min(1, "Id is required"),
    foreignKeyValue: z.string().trim().min(1, "Foreign key is required"),
    integrationId: z.string().trim().min(1, "Integration id is required"),
  }),
  alertSchema: z.object({
    id: z.string().trim().min(1, "Id is required"),
    name: z.string().trim().min(1, "Name is required"),
    foreignKeyProps: z
      .array(z.string())
      .min(1, "Foreign keys props is required"),
    statusMappingResolved: z.string().optional(),
    statusMappingWarning: z.string().optional(),
    statusMappingCritical: z.string().optional(),
    system: z.boolean().default(false),
    statusProp: z.string().min(1, "Status prop is required"),
    description: z.string(),
  }),
  userRole,
  userStatus,
  user: z.object({
    id: z.string().trim().min(1, "Id is required"),
    email: z.email(),
    firstName: z.string(),
    lastName: z.string(),
    role: userRole.default("admin"),
    status: userStatus.default("pending"),
  }),
};
